import {ArrowDownward, ArrowUpward, ContentPaste, Delete, DeleteOutline, DragIndicator, FileCopy} from '@mui/icons-material';
import {Box, Button, Grid, IconButton, styled, Typography} from '@mui/material';
import React, {useEffect, useState} from 'react';
import {DragDropContext, Draggable, Droppable} from 'react-beautiful-dnd';
import {useFormContext, useWatch} from 'react-hook-form';
import {useSelector} from 'react-redux';
import authSelectors from 'src/modules/auth/authSelectors';
import {isPlastyvergTenant} from 'src/modules/tarea/dynamicSubsectionPdfGrouping';
import InputFormItem from 'src/view/shared/form/items/InputFormItem';
import InputNumberFormItem from 'src/view/shared/form/items/InputNumberFormItem';
import SelectFormItem from 'src/view/shared/form/items/SelectFormItem';
import UnidadMedidaAutocompleteFormItem from 'src/view/unidadMedida/autocomplete/UnidadMedidaAutocompleteFormItem';

import {Tooltip} from '@mui/material';
import {makeStyles} from '@mui/styles';
import moment from 'moment-timezone';
import {i18n, i18nExists} from 'src/i18n';
import incidentEnumerators from 'src/modules/incident/incidentEnumerators';
import perfilEmpleadoEnumerators from 'src/modules/perfilEmpleado/perfilEmpleadoEnumerators';
import CustomAccordion from 'src/view/shared/CustomAccordion';
import CheckboxFormItem from 'src/view/shared/form/items/CheckboxFormItem';
import InputNumberRangeFormItem from 'src/view/shared/form/items/InputNumberRangeFormItem';
import TagsFormItem from 'src/view/shared/form/items/TagsFormItem';
import {styles as materialUiStyles} from 'src/view/shared/form/items/shared/reactSelectMaterialUi';
import Message from 'src/view/shared/message';
import ConfirmModal from 'src/view/shared/modals/ConfirmModal';

const useMaterialStyles = makeStyles(materialUiStyles as any);

const OnHoverBox = styled(Box)`
  display: flex;
  flexdirection: row;
  gap: 8px;
  opacity: 0.2;
  &:hover {
    opacity: 1;
  }
`;

const ALT_TREE_CLIPBOARD_TYPE = 'planiwise.alternativeTree';

/** Pasar a false para ofrecer las preguntas del parte Blumar en todos los tenants. */
const hideBlumarStuffForOthers = true;
const BLUMAR_SSO_TENANT_ID = '62a8b6dd2323505723b7e92b';
const BLUMAR_INCIDENT_ANSWER_TYPES = new Set([
  'incident_contactPhone',
  'incident_directManagerPosition',
  'incident_propertyDamage',
  'incident_workPerformed',
  'incident_atSea',
  'incident_causingAgent',
  'incident_injuryType',
  'incident_narratorName',
  'incident_narratorRole',
  'incident_causalEffect',
  'incident_causalProblem',
  'incident_causalCause1',
  'incident_causalCause2',
  'incident_causalCause3',
  'incident_investigationConclusion',
  'incident_incidentWeekday',
  'incident_accionesSubestandar',
  'incident_condicionesSubestandar',
  'incident_tipoAccidente',
]);
const ALT_TREE_CLIPBOARD_VERSION = 1;
const MAX_TREE_DEPTH = 3;

const generateNodeId = () => `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

const normalizeAlternativeNodes = (nodes: any, depth = 1) => {
  if (!Array.isArray(nodes)) {
    return [];
  }

  return nodes
    .map((node, index) => {
      const labelValue = `${node?.label ?? node?.value ?? ''}`.trim();
      if (!labelValue) {
        return null;
      }

      const normalized: any = {
        id: `${node?.id ?? `${generateNodeId()}_${index}`}`,
        label: labelValue,
        children: [],
      };

      if (depth < MAX_TREE_DEPTH && Array.isArray(node?.children)) {
        normalized.children = normalizeAlternativeNodes(node.children, depth + 1);
      }

      return normalized;
    })
    .filter(Boolean);
};

const parseClipboardRows = (rawText: string) => {
  if (!rawText?.trim()) {
    return [];
  }

  return rawText
    .split(/\r?\n/)
    .map((line) => line.split('\t').map((cell) => cell.trim()))
    .map((cells) => cells.filter((cell) => !!cell))
    .filter((cells) => cells.length > 0);
};

const ROOT_DROPPABLE_ID = 'alt_root';

const parseDroppableParentPath = (droppableId: string) => {
  if (droppableId === ROOT_DROPPABLE_ID) {
    return [];
  }
  const [type, iRaw, jRaw] = droppableId.split(':');
  if (type === 'l1' && iRaw !== undefined) {
    return [Number(iRaw)];
  }
  if (type === 'l2' && iRaw !== undefined && jRaw !== undefined) {
    return [Number(iRaw), Number(jRaw)];
  }
  return null;
};

const getNodeByPath = (tree: any = [], path: number[] = []) => {
  if (!Array.isArray(tree)) {
    return null;
  }
  if (!path.length) {
    return null;
  }
  if (path.length === 1) {
    return tree[path[0]] || null;
  }
  if (path.length === 2) {
    return tree[path[0]]?.children?.[path[1]] || null;
  }
  if (path.length === 3) {
    return tree[path[0]]?.children?.[path[1]]?.children?.[path[2]] || null;
  }
  return null;
};

const startsWithPath = (fullPath: number[] = [], prefix: number[] = []) => {
  if (prefix.length > fullPath.length) {
    return false;
  }
  return prefix.every((value, index) => fullPath[index] === value);
};

const getSubtreeHeight = (node: any) => {
  if (!node || !Array.isArray(node.children) || !node.children.length) {
    return 1;
  }
  return 1 + Math.max(...node.children.map((child) => getSubtreeHeight(child)));
};

const resetHorizontalScrollAfterDrag = () => {
  if (typeof window !== 'undefined') {
    window.scrollTo({left: 0, top: window.scrollY});
  }
  if (typeof document !== 'undefined') {
    const modelForm = document.querySelector('#container-form form') as HTMLElement | null;
    if (modelForm && modelForm.scrollLeft > 0) {
      modelForm.scrollTo({left: 0, behavior: 'smooth'});
      return;
    }
    const fallbackForm = document.querySelector('form') as HTMLElement | null;
    if (fallbackForm && fallbackForm.scrollLeft > 0) {
      fallbackForm.scrollTo({left: 0, behavior: 'smooth'});
    }
  }
};

export default function PreguntaFormularioForm(props) {
  const {
    fieldsLength,
    expanded,
    handleChangeAccordion,
    handleExitedAccordion,
    append,
    insert,
    remove,
    move,
    item,
    indexPregunta,
    name,
    control,
    register,
  } = props;
  const {getValues, setValue} = useFormContext();
  const currentTenant = useSelector(authSelectors.selectCurrentTenant);
  const showBlumarIncidentFields =
    !hideBlumarStuffForOthers || currentTenant?.id === BLUMAR_SSO_TENANT_ID;

  //console.log('%c⧭ PreguntaFormularioForm: pregunta', 'color: #7f2150', item, getValues(name));
  item.nPregunta = indexPregunta + 1;
  useEffect(() => {
    register(`${name}.nPregunta`);
    register(`${name}.pregunta`);
    register(`${name}.tipoRespuesta`);
    register(`${name}.link`);
    register(`${name}.tieneLink`);
    register(`${name}.tieneComentario`);
    register(`${name}.commentLabel`);
    register(`${name}.tienePuntaje`);
    register(`${name}.puntaje`);
    register(`${name}.unidadMedida`);
    register(`${name}.alternativasCheckbox`);
    register(`${name}.alternativas`);
    register(`${name}.respuestaCorrecta`);
    register(`${name}.allowDateBefore`);
    register(`${name}.restrictByLocation`);
    register(`${name}.hiddenInPdf`);
    register(`${name}.haveCriticality`);
    register(`${name}.havePhotos`);
    register(`${name}.haveFiles`);

    register(`${name}.keyPointer`); // El campo de modulo incidentes donde se guardará la respuesta al responder el formulario

    register(`${name}.hasAlertNumberRange`);
    register(`${name}.multipleAnswersType`);
    register(`${name}.allowMultipleAnswers`);
    register(`${name}.onlyOneOption`);
    register(`${name}.allowOthersOption`);
    register(`${name}.defaultFirstOption`);
    register(`${name}.alternativeTree`);
    register(`${name}.alternativeTreeDisplayLevels`);
    register(`${name}.isRequired`);
    register(`${name}.allowNotApplicable`);
    register(`${name}.numberFieldMin`);
    register(`${name}.numberFieldMax`);
    register(`${name}.ammountMin`);
    register(`${name}.ammountMax`);

    // set values //
    setValue(`${name}.nPregunta`, indexPregunta + 1);
    setValue(`${name}.tieneLink`, item.tieneLink || (item.link ? true : false));
    setValue(`${name}.link`, item.link || '');
    if (item.pregunta) {
      const tituloPregunta = item.pregunta.titulo || item.pregunta.label;
      setValue(`${name}.pregunta.titulo`, tituloPregunta);
      setValue(`${name}.pregunta.label`, tituloPregunta);
    }
    if (!item.tipoRespuesta) {
      setValue(`${name}.tipoRespuesta`, 'textField');
    }
  }, []);

  let tipoRespuestaOptions = [
    {
      value: 'textField',
      label: 'Campo de Texto',
    },
    {
      value: 'numberField',
      label: 'Campo de Número',
    },
    {
      value: 'multipleChoice',
      label: 'Lista Desplegable',
    },
    {
      value: 'multiCheckbox',
      label: 'Lista de Checkbox',
    },
    {
      value: 'checkbox',
      label: 'Checkbox',
    },
    {
      value: 'inspectionCheck',
      label: 'Inspección',
    },
    {
      value: 'yesNo',
      label: 'Si, No, N/A',
    },
    {
      value: 'ammount',
      label: 'Monto',
    },
    {
      value: 'registrationNumber',
      label: 'RUT',
    },
    {
      value: 'barcode',
      label: 'Código de Barra',
    },
    {
      value: 'date',
      label: 'Fecha',
    },
    {
      value: 'time',
      label: 'Hora',
    },
    {
      value: 'day',
      label: 'Día',
    },
    {
      value: 'month',
      label: 'Mes',
    },
    {
      value: 'year',
      label: 'Año',
    },
    {
      value: 'location',
      label: 'Ubicación',
    },
    {
      value: 'thirdParty',
      label: 'Tercero',
    },
    {
      value: 'asset',
      label: 'Activo',
    },
    {
      value: 'perfilEmpleado',
      label: 'Colaborador',
    },
    {
      value: 'usuario',
      label: 'Usuario',
    },
    {
      value: 'asignadorFuncion',
      label: 'Teams',
    },
    {
      value: 'project',
      label: 'Proyecto',
    },
    ...(isPlastyvergTenant(currentTenant?.id)
      ? [
          {
            value: 'articulo',
            label: 'Artículo',
          },
        ]
      : []),
    {
      value: 'files',
      label: 'Foto / Documento',
    },
    {
      value: 'textFromImage',
      label: 'Texto desde Imagen',
    },
    {
      value: 'signature',
      label: 'Firma',
    },
    {
      value: 'meterReading',
      label: 'Lectura de Medidor',
    },
  ];

  const mapIncidentEnum = (name, options) => {
    return options.map((option) => ({
      id: option,
      label: i18n(`entities.incident.enumerators.${name}.${option}`),
    }));
  };

  const mapPerfilEmpleadoEnum = (nameKey, options) => {
    return options.map((option) => {
      const path = `entities.perfilEmpleado.enumerators.${nameKey}.${option}`;
      return {
        id: option,
        label: i18nExists(path) ? i18n(path) : option,
      };
    });
  };

  const mapIncidentAlternativeTreePath = (options) => {
    const SEP = '\\';

    const buildTree = (obj, path = '') => {
      return Object.keys(obj).map((key) => {
        const currentPath = path ? `${path}${SEP}${key}` : key;
        const value = currentPath;
        const label = key;

        if (Array.isArray(obj[key])) {
          return {
            value,
            label,
            children: obj[key].map((subValue) => {
              const leafPath = `${currentPath}${SEP}${subValue}`;
              return {
                parent: currentPath,
                value: leafPath,
                label: subValue,
              };
            }),
          };
        }

        return {
          value,
          label,
          children: buildTree(obj[key], currentPath),
        };
      });
    };

    return buildTree(options);
  };

  const mapIncidentAlternativeTree = (options) => {
    const buildTree = (obj) => {
      return Object.keys(obj).map((key) => {
        if (Array.isArray(obj[key])) {
          return {
            value: key,
            label: key,
            children: obj[key].map((subValue) => ({
              parent: key,
              value: subValue,
              label: subValue,
            })),
          };
        } else {
          return {
            value: key,
            label: key,
            children: buildTree(obj[key]),
          };
        }
      });
    };
    return buildTree(options);
  };

  const mapIncidentAlternativeTreeRiesgo = () => {
    return Object.keys(incidentEnumerators.riesgo).reduce((acc, keyCatRiesgo) => {
      const riesgos = incidentEnumerators.riesgo[keyCatRiesgo];
      acc.push({
        children: riesgos.map((riesgoItem) => ({
          value: riesgoItem.riesgo,
          label: riesgoItem.riesgo,
          codigo: riesgoItem.codigo,
          tipo: riesgoItem.tipo,
          familia: riesgoItem.familia,
        })),
        label: keyCatRiesgo,
      });
      return acc;
    }, [] as any[]);
  };

  const tipoRespuestaIncidenteOptions = [
    // --- los tuyos (tal cual) ---
    {
      value: 'incident_incidentCode',
      label: i18n('entities.incident.fields.incidentCode') + ` (${i18n('entities.incident.name')})`,
      responseType: 'textField',
    },
    {
      value: 'incident_withLostDays',
      label: i18n('entities.incident.fields.withLostDays') + ` (${i18n('entities.incident.name')})`,
      responseType: 'checkbox',
    },
    {
      value: 'incident_initialClassification',
      label: i18n('entities.incident.fields.initialClassification') + ` (${i18n('entities.incident.name')})`,
      responseType: 'multipleChoice',
      subOptions: mapIncidentEnum('initialClassification', incidentEnumerators.initialClassification),
    },
    {
      value: 'incident_finalClassification',
      label: i18n('entities.incident.fields.finalClassification') + ` (${i18n('entities.incident.name')})`,
      responseType: 'multipleChoice',
      subOptions: mapIncidentEnum('finalClassification', incidentEnumerators.finalClassification),
    },
    {
      value: 'incident_severity',
      label: i18n('entities.incident.fields.severity') + ` (${i18n('entities.incident.name')})`,
      responseType: 'multipleChoice',
      subOptions: mapIncidentEnum('severity', incidentEnumerators.severity),
    },
    {
      value: 'incident_label',
      label: i18n('entities.incident.fields.label') + ` (${i18n('entities.incident.name')})`,
      responseType: 'textField',
    },
    {
      value: 'incident_incidentDate',
      label: i18n('entities.incident.fields.incidentDate') + ` (${i18n('entities.incident.name')})`,
      responseType: 'date',
    },
    {
      value: 'incident_incidentDateTime',
      label: i18n('entities.incident.fields.incidentDateTime') + ` (${i18n('entities.incident.name')})`,
      responseType: 'time',
    },
    {
      value: 'incident_dateFrom',
      label: i18n('entities.incident.fields.dateFrom') + ` (${i18n('entities.incident.name')})`,
      responseType: 'date',
    },
    {
      value: 'incident_dateFromTime',
      label: i18n('entities.incident.fields.dateFromTime') + ` (${i18n('entities.incident.name')})`,
      responseType: 'time',
    },
    {
      value: 'incident_dateTo',
      label: i18n('entities.incident.fields.dateTo') + ` (${i18n('entities.incident.name')})`,
      responseType: 'date',
    },
    {
      value: 'incident_responsibleAdvisor',
      label: i18n('entities.incident.fields.responsibleAdvisor') + ` (${i18n('entities.incident.name')})`,
      responseType: 'perfilEmpleado',
    },
    {
      value: 'incident_areaManager',
      label: i18n('entities.incident.fields.areaManager') + ` (${i18n('entities.incident.name')})`,
      responseType: 'usuario',
    },
    {
      value: 'incident_incidentLocation',
      label: i18n('entities.incident.fields.incidentLocation') + ` (${i18n('entities.incident.name')})`,
      responseType: 'location',
    },
    {
      value: 'incident_incidentDescription',
      label: i18n('entities.incident.fields.incidentDescription') + ` (${i18n('entities.incident.name')})`,
      responseType: 'textField',
    },
    {
      value: 'incident_narrativeAccount',
      label: i18n('entities.incident.fields.narrativeAccount') + ` (${i18n('entities.incident.name')})`,
      responseType: 'textField',
    },
    {
      value: 'incident_incidentStatus',
      label: i18n('entities.incident.fields.incidentStatus') + ` (${i18n('entities.incident.name')})`,
      responseType: 'multipleChoice',
      subOptions: mapIncidentEnum('incidentStatus', incidentEnumerators.incidentStatus),
    },
    {
      value: 'incident_company',
      label: i18n('entities.incident.fields.company') + ` (${i18n('entities.incident.name')})`,
      responseType: 'textField',
    },
    {
      value: 'incident_employee',
      label: i18n('entities.incident.fields.employee') + ` (${i18n('entities.incident.name')})`,
      responseType: 'perfilEmpleado',
    },
    // --- faltantes según schema/envíos anteriores ---
    {
      value: 'incident_daysAdjustment',
      label: i18n('entities.incident.fields.daysAdjustment') + ` (${i18n('entities.incident.name')})`,
      responseType: 'numberField',
    },
    {
      value: 'incident_entryType',
      label: i18n('entities.incident.fields.entryType') + ` (${i18n('entities.incident.name')})`,
      responseType: 'multipleChoice',
      subOptions: mapIncidentEnum('entryType', incidentEnumerators.entryType),
    },
    // riesgos (strings)
    {
      value: 'incident_riesgo',
      label: i18n('entities.incident.fields.riesgo') + ` (${i18n('entities.incident.name')})`,
      responseType: 'multipleChoice',
      subOptions: mapIncidentAlternativeTreeRiesgo(),
      alternativeTree: true,
    },
    // enumerators
    // {
    //   value: 'incident_origin',
    //   label: i18n('entities.incident.fields.origin') + ` (${i18n('entities.incident.name')})`,
    //   responseType: 'multipleChoice',
    //   subOptions: mapIncidentEnum('origin', incidentEnumerators.origin.filter(Boolean)),
    // },
    {
      value: 'incident_contractType',
      label: i18n('entities.incident.fields.contractType') + ` (${i18n('entities.incident.name')})`,
      responseType: 'multipleChoice',
      subOptions: mapPerfilEmpleadoEnum('contractType', perfilEmpleadoEnumerators.contractType),
    },
    // narrativa extra
    {
      value: 'incident_narrativeWhere',
      label: i18n('entities.incident.fields.narrativeWhere') + ` (${i18n('entities.incident.name')})`,
      responseType: 'textField',
    },
    {
      value: 'incident_narrativeHow',
      label: i18n('entities.incident.fields.narrativeHow') + ` (${i18n('entities.incident.name')})`,
      responseType: 'textField',
    },
    {
      value: 'incident_narrativeWhen',
      label: i18n('entities.incident.fields.narrativeWhen') + ` (${i18n('entities.incident.name')})`,
      responseType: 'textField',
    },
    // relaciones varias
    {
      value: 'incident_contractorCompany',
      label: i18n('entities.incident.fields.contractorCompany') + ` (${i18n('entities.incident.name')})`,
      responseType: 'thirdParty',
    },
    {
      value: 'incident_management',
      label: i18n('entities.incident.fields.management') + ` (${i18n('entities.incident.name')})`,
      responseType: 'textField',
    },
    // bloque nuevo
    {
      value: 'incident_submissionDate',
      label: i18n('entities.incident.fields.submissionDate') + ` (${i18n('entities.incident.name')})`,
      responseType: 'date',
    },
    {
      value: 'incident_reportDate',
      label: i18n('entities.incident.fields.reportDate') + ` (${i18n('entities.incident.name')})`,
      responseType: 'date',
    },
    // IncidentFormNew: horarios, trabajador, organización, riesgo (campos visibles)
    {
      value: 'incident_horaIngresoTrabajo',
      label: i18n('entities.incident.fields.horaIngresoTrabajo') + ` (${i18n('entities.incident.name')})`,
      responseType: 'time',
    },
    {
      value: 'incident_horaSalidaTrabajo',
      label: i18n('entities.incident.fields.horaSalidaTrabajo') + ` (${i18n('entities.incident.name')})`,
      responseType: 'time',
    },
    {
      value: 'incident_fullName',
      label: i18n('entities.incident.fields.fullName') + ` (${i18n('entities.incident.name')})`,
      responseType: 'textField',
    },
    {
      value: 'incident_registrationNumber',
      label: i18n('entities.incident.fields.registrationNumber') + ` (${i18n('entities.incident.name')})`,
      responseType: 'textField',
    },
    {
      value: 'incident_email',
      label: i18n('entities.incident.fields.email') + ` (${i18n('entities.incident.name')})`,
      responseType: 'textField',
    },
    {
      value: 'incident_jobPosition',
      label: i18n('entities.incident.fields.jobPosition') + ` (${i18n('entities.incident.name')})`,
      responseType: 'textField',
    },
    {
      value: 'incident_companyStartDate',
      label: i18n('entities.incident.fields.companyStartDate') + ` (${i18n('entities.incident.name')})`,
      responseType: 'date',
    },
    {
      value: 'incident_contractEndDate',
      label: i18n('entities.incident.fields.contractEndDate') + ` (${i18n('entities.incident.name')})`,
      responseType: 'date',
    },
    {
      value: 'incident_companyExperienceYears',
      label: i18n('entities.incident.fields.companyExperienceYears') + ` (${i18n('entities.incident.name')})`,
      responseType: 'numberField',
    },
    {
      value: 'incident_companyExperienceMonths',
      label: i18n('entities.incident.fields.companyExperienceMonths') + ` (${i18n('entities.incident.name')})`,
      responseType: 'numberField',
    },
    {
      value: 'incident_companyExperienceDays',
      label: i18n('entities.incident.fields.companyExperienceDays') + ` (${i18n('entities.incident.name')})`,
      responseType: 'numberField',
    },
    {
      value: 'incident_jobStartDate',
      label: i18n('entities.incident.fields.jobStartDate') + ` (${i18n('entities.incident.name')})`,
      responseType: 'date',
    },
    {
      value: 'incident_workExperienceYears',
      label: i18n('entities.incident.fields.workExperienceYears') + ` (${i18n('entities.incident.name')})`,
      responseType: 'numberField',
    },
    {
      value: 'incident_workExperienceMonths',
      label: i18n('entities.incident.fields.workExperienceMonths') + ` (${i18n('entities.incident.name')})`,
      responseType: 'numberField',
    },
    {
      value: 'incident_workExperienceDays',
      label: i18n('entities.incident.fields.workExperienceDays') + ` (${i18n('entities.incident.name')})`,
      responseType: 'numberField',
    },
    {
      value: 'incident_ageYears',
      label: i18n('entities.incident.fields.ageYears') + ` (${i18n('entities.incident.name')})`,
      responseType: 'numberField',
    },
    {
      value: 'incident_gender',
      label: i18n('entities.incident.fields.gender') + ` (${i18n('entities.incident.name')})`,
      responseType: 'multipleChoice',
      subOptions: mapPerfilEmpleadoEnum('gender', perfilEmpleadoEnumerators.gender),
    },
    {
      value: 'incident_birthDate',
      label: i18n('entities.incident.fields.birthDate') + ` (${i18n('entities.incident.name')})`,
      responseType: 'date',
    },
    {
      value: 'incident_nationality',
      label: i18n('entities.incident.fields.nationality') + ` (${i18n('entities.incident.name')})`,
      responseType: 'multipleChoice',
      subOptions: mapPerfilEmpleadoEnum('nationality', perfilEmpleadoEnumerators.nationality),
    },
    {
      value: 'incident_injuredZoneSpecific',
      label: i18n('entities.incident.fields.injuredZoneSpecific') + ` (${i18n('entities.incident.name')})`,
      responseType: 'multipleChoice',
      subOptions: mapIncidentAlternativeTree(incidentEnumerators.zonaAfectada),
      alternativeTree: true,
    },
    {
      value: 'incident_locations', // centro de trabajo
      label: i18n('entities.incident.fields.locations') + ` (${i18n('entities.incident.name')})`,
      responseType: 'location',
      isMultiple: true,
    },
    {
      value: 'incident_age',
      label: i18n('entities.incident.fields.age') + ` (${i18n('entities.incident.name')})`,
      responseType: 'numberField',
    },
    {
      value: 'incident_workExperience',
      label: i18n('entities.incident.fields.workExperience') + ` (${i18n('entities.incident.name')})`,
      responseType: 'numberField',
    },
    // arrays de strings
    {
      value: 'incident_causaInmediata1',
      label: i18n('entities.incident.fields.causaInmediata1') + ` (${i18n('entities.incident.name')})`,
      responseType: 'multipleChoice',
      subOptions: mapIncidentAlternativeTreePath(incidentEnumerators.causasInmediatas),
      alternativeTree: true,
    },
    {
      value: 'incident_causaInmediata2',
      label: i18n('entities.incident.fields.causaInmediata2') + ` (${i18n('entities.incident.name')})`,
      responseType: 'multipleChoice',
      subOptions: mapIncidentAlternativeTreePath(incidentEnumerators.causasInmediatas),
      alternativeTree: true,
    },
    {
      value: 'incident_causaInmediata3',
      label: i18n('entities.incident.fields.causaInmediata3') + ` (${i18n('entities.incident.name')})`,
      responseType: 'multipleChoice',
      subOptions: mapIncidentAlternativeTreePath(incidentEnumerators.causasInmediatas),
      alternativeTree: true,
    },
    {
      value: 'incident_causaBasica1',
      label: i18n('entities.incident.fields.causaBasica1') + ` (${i18n('entities.incident.name')})`,
      responseType: 'multipleChoice',
      subOptions: mapIncidentAlternativeTreePath(incidentEnumerators.causasBasicas),
      alternativeTree: true,
    },
    {
      value: 'incident_causaBasica2',
      label: i18n('entities.incident.fields.causaBasica2') + ` (${i18n('entities.incident.name')})`,
      responseType: 'multipleChoice',
      subOptions: mapIncidentAlternativeTreePath(incidentEnumerators.causasBasicas),
      alternativeTree: true,
    },
    {
      value: 'incident_causaBasica3',
      label: i18n('entities.incident.fields.causaBasica3') + ` (${i18n('entities.incident.name')})`,
      responseType: 'multipleChoice',
      subOptions: mapIncidentAlternativeTreePath(incidentEnumerators.causasBasicas),
      alternativeTree: true,
    },
    {
      value: 'incident_medidasDeControl',
      label: i18n('entities.incident.fields.medidasDeControl') + ` (${i18n('entities.incident.name')})`,
      responseType: 'textField',
      isMultiple: true,
    },
    {
      value: 'incident_medidas_control_medida',
      label: `Medida (tabla medidas de control) (${i18n('entities.incident.name')})`,
      responseType: 'textField',
      isMultiple: true,
    },
    {
      value: 'incident_medidas_control_responsable',
      label: `Responsable (tabla medidas de control) (${i18n('entities.incident.name')})`,
      responseType: 'textField',
      isMultiple: true,
    },
    {
      value: 'incident_testigos',
      label: i18n('entities.incident.fields.testigos') + ` (${i18n('entities.incident.name')})`,
      responseType: 'textField',
      isMultiple: true,
    },
    {
      value: 'incident_contactPhone',
      label: i18n('entities.incident.fields.contactPhone') + ` (${i18n('entities.incident.name')})`,
      responseType: 'textField',
    },
    {
      value: 'incident_directManagerPosition',
      label: i18n('entities.incident.fields.directManagerPosition') + ` (${i18n('entities.incident.name')})`,
      responseType: 'textField',
    },
    {
      value: 'incident_propertyDamage',
      label: i18n('entities.incident.fields.propertyDamage') + ` (${i18n('entities.incident.name')})`,
      responseType: 'yesNo',
    },
    {
      value: 'incident_workPerformed',
      label: i18n('entities.incident.fields.workPerformed') + ` (${i18n('entities.incident.name')})`,
      responseType: 'textField',
    },
    {
      value: 'incident_atSea',
      label: i18n('entities.incident.fields.atSea') + ` (${i18n('entities.incident.name')})`,
      responseType: 'yesNo',
    },
    {
      value: 'incident_causingAgent',
      label: i18n('entities.incident.fields.causingAgent') + ` (${i18n('entities.incident.name')})`,
      responseType: 'textField',
    },
    {
      value: 'incident_injuryType',
      label: i18n('entities.incident.fields.injuryType') + ` (${i18n('entities.incident.name')})`,
      responseType: 'multipleChoice',
      subOptions: mapIncidentEnum('injuryType', incidentEnumerators.injuryType),
    },
    {
      value: 'incident_narratorName',
      label: i18n('entities.incident.fields.narratorName') + ` (${i18n('entities.incident.name')})`,
      responseType: 'textField',
    },
    {
      value: 'incident_narratorRole',
      label: i18n('entities.incident.fields.narratorRole') + ` (${i18n('entities.incident.name')})`,
      responseType: 'textField',
    },
    {
      value: 'incident_causalEffect',
      label: i18n('entities.incident.fields.causalEffect') + ` (${i18n('entities.incident.name')})`,
      responseType: 'textField',
    },
    {
      value: 'incident_causalProblem',
      label: i18n('entities.incident.fields.causalProblem') + ` (${i18n('entities.incident.name')})`,
      responseType: 'textField',
    },
    {
      value: 'incident_causalCause1',
      label: i18n('entities.incident.fields.causalCause1') + ` (${i18n('entities.incident.name')})`,
      responseType: 'textField',
    },
    {
      value: 'incident_causalCause2',
      label: i18n('entities.incident.fields.causalCause2') + ` (${i18n('entities.incident.name')})`,
      responseType: 'textField',
    },
    {
      value: 'incident_causalCause3',
      label: i18n('entities.incident.fields.causalCause3') + ` (${i18n('entities.incident.name')})`,
      responseType: 'textField',
    },
    {
      value: 'incident_investigationConclusion',
      label: i18n('entities.incident.fields.investigationConclusion') + ` (${i18n('entities.incident.name')})`,
      responseType: 'textField',
    },
    {
      value: 'incident_incidentWeekday',
      label: i18n('entities.incident.fields.incidentWeekday') + ` (${i18n('entities.incident.name')})`,
      responseType: 'day',
    },
    {
      value: 'incident_accionesSubestandar',
      label: i18n('entities.incident.fields.accionesSubestandar') + ` (${i18n('entities.incident.name')})`,
      responseType: 'multiCheckbox',
      allowOthersOption: true,
      subOptions: incidentEnumerators.accionesSubestandar.map((option) => ({id: option, label: option})),
    },
    {
      value: 'incident_condicionesSubestandar',
      label: i18n('entities.incident.fields.condicionesSubestandar') + ` (${i18n('entities.incident.name')})`,
      responseType: 'multiCheckbox',
      allowOthersOption: true,
      subOptions: incidentEnumerators.condicionesSubestandar.map((option) => ({id: option, label: option})),
    },
    {
      value: 'incident_tipoAccidente',
      label: i18n('entities.incident.fields.tipoAccidente') + ` (${i18n('entities.incident.name')})`,
      responseType: 'multiCheckbox',
      allowOthersOption: true,
      subOptions: incidentEnumerators.tipoAccidente.map((option) => ({id: option, label: option})),
    },
    // archivos
    {
      value: 'incident_testigosImages',
      label: i18n('entities.incident.fields.testigosImages') + ` (${i18n('entities.incident.name')})`,
      responseType: 'files',
    },
    {
      value: 'incident_images',
      label: i18n('entities.incident.fields.images') + ` (${i18n('entities.incident.name')})`,
      responseType: 'files',
    },
  ];

  const watchAlternativeTree = useWatch({
    name: `${name}.alternativeTree`,
    control,
    defaultValue: item.alternativeTree,
  });

  const watchAlternatives = useWatch({
    name: `${name}.alternativas`,
    control,
    defaultValue: item.alternativas,
  });
  const watchAlternativasCheckbox = useWatch({
    name: `${name}.alternativasCheckbox`,
    control,
    defaultValue: item.alternativasCheckbox,
  });
  const [pendingTreePaste, setPendingTreePaste] = useState<any>(null);

  const watchHasAlertNumberRange = useWatch({
    name: `${name}.hasAlertNumberRange`,
    control,
    defaultValue: item.hasAlertNumberRange,
  });

  const clasificacionValue = useWatch({name: 'clasificacion', control});
  const watchKeyPointer = useWatch({name: `${name}.keyPointer`, control, defaultValue: item.keyPointer});
  const watchTipoRespuesta = useWatch({name: `${name}.tipoRespuesta`, control});
  //console.log('%c⧭ watchTipoRespuesta', 'color: #735656', watchTipoRespuesta);
  if (clasificacionValue === 'incidente' || clasificacionValue === 'investigacionIncidente') {
    tipoRespuestaIncidenteOptions.forEach((incidentOption) => {
      if ('incident_' + watchKeyPointer === incidentOption.value) {
        if (watchTipoRespuesta !== incidentOption.value) {
          //console.log('%c⧭', 'color: #ffcc00', 'Convert', watchTipoRespuesta, 'To', incidentOption.value);
          setValue(`${name}.tipoRespuesta`, incidentOption.value);
        }
      }
    });
    const visibleTipoRespuestaIncidenteOptions = tipoRespuestaIncidenteOptions.filter(
      (incidentOption) => showBlumarIncidentFields || !BLUMAR_INCIDENT_ANSWER_TYPES.has(incidentOption.value),
    );
    tipoRespuestaOptions = tipoRespuestaOptions.concat(visibleTipoRespuestaIncidenteOptions);
  }
  const watchName = useWatch({name: `${name}`});
  //console.log('%c⧭ pregunta', 'color: #e50000', {watchName});
  const watchTienePuntaje = useWatch({name: `${name}.tienePuntaje`, control, defaultValue: item.tienePuntaje});
  const watchTieneLink = useWatch({name: `${name}.tieneLink`, control, defaultValue: item.tieneLink || !!item.link});
  const watchPregunta = useWatch({name: `${name}.pregunta`, control, defaultValue: item.pregunta});
  // const watchLink = useWatch({name: `${name}.link`, control, defaultValue: item.link});
  // console.log('%c⧭', 'color: #408059', {watchLink});
  const watchTieneComentario = useWatch({name: `${name}.tieneComentario`, control, defaultValue: item.tieneComentario});

  const [recordToDelete, setRecordToDelete] = useState(null);

  const doCloseDeleteConfirmModal = () => {
    setRecordToDelete(null);
  };

  const doDeleteRecord = (index) => {
    remove(index);
    doCloseDeleteConfirmModal();
  };

  const doOpenDeleteConfirmModal = (index) => {
    setRecordToDelete(index);
  };

  const [recordToCopy, setRecordToCopy] = useState(null);
  const doCloseCopyConfirmModal = () => {
    setRecordToCopy(null);
  };

  const resetAlternativeIds = (alternatives: any[] = [], depth = 1) => {
    if (!Array.isArray(alternatives)) {
      return [];
    }
    return alternatives.map((alt) => {
      const normalized: any = {
        id: generateNodeId(),
        label: `${alt?.label ?? ''}`.trim(),
        children: [],
      };
      if (depth < MAX_TREE_DEPTH) {
        normalized.children = resetAlternativeIds(alt?.children || [], depth + 1);
      }
      return normalized;
    });
  };

  const appendRowsToTreeNode = (rows: string[][], target, fieldName: string) => {
    const currentTree = getValues(fieldName) || [];
    const updated = [...currentTree];
    if (!target || !Array.isArray(updated) || !updated.length) {
      Message.error(i18n('common.treePasteNeedFocusedNode'));
      return;
    }

    const ensureChildren = (node) => {
      if (!Array.isArray(node.children)) {
        node.children = [];
      }
      return node.children;
    };

    const addUniqueChild = (children, label) => {
      const cleanLabel = `${label ?? ''}`.trim();
      if (!cleanLabel) {
        return {node: null, created: false};
      }
      let existing = children.find((child) => `${child?.label ?? ''}`.trim() === cleanLabel);
      if (existing) {
        return {node: existing, created: false};
      }
      existing = {id: generateNodeId(), label: cleanLabel, children: []};
      children.push(existing);
      return {node: existing, created: true};
    };

    const rootNode = updated[target.i];
    if (!rootNode) {
      Message.error(i18n('common.treePasteNeedFocusedNode'));
      return;
    }

    let inserted = 0;
    rows.forEach((row) => {
      const cells = Array.isArray(row) ? row.map((cell) => `${cell ?? ''}`.trim()).filter(Boolean) : [];
      if (!cells.length) {
        return;
      }

      if (target.level === 1) {
        const level2Children = ensureChildren(rootNode);
        const level2Result = addUniqueChild(level2Children, cells[0]);
        if (!level2Result.node) {
          return;
        }
        if (level2Result.created) {
          inserted += 1;
        }
        if (cells[1]) {
          const level3Result = addUniqueChild(ensureChildren(level2Result.node), cells[1]);
          if (level3Result.created) {
            inserted += 1;
          }
        }
        return;
      }

      if (target.level === 2) {
        const level2Node = ensureChildren(rootNode)[target.j];
        if (!level2Node) {
          return;
        }
        const level3Result = addUniqueChild(ensureChildren(level2Node), cells[0]);
        if (level3Result.created) {
          inserted += 1;
        }
        return;
      }

      if (target.level === 3) {
        const level2Node = ensureChildren(rootNode)[target.j];
        if (!level2Node) {
          return;
        }
        const siblings = ensureChildren(level2Node);
        const cleanLabel = cells[0];
        const exists = siblings.some((child) => `${child?.label ?? ''}`.trim() === cleanLabel);
        if (!exists && cleanLabel) {
          const insertIndex = Math.min((target.k ?? siblings.length - 1) + 1, siblings.length);
          siblings.splice(insertIndex, 0, {id: generateNodeId(), label: cleanLabel, children: []});
          target.k = insertIndex;
          inserted += 1;
        }
      }
    });

    setValue(fieldName, updated);
    Message.success(i18n('common.treePasteRowsSuccess', inserted));
  };

  const appendFlatAlternatives = (labels: string[], fieldName: string) => {
    const current = getValues(fieldName);
    const next = Array.isArray(current) ? [...current] : [];
    labels.forEach((label) => {
      const slug = label.toLowerCase().replace(/\W/g, '');
      next.push({
        id: slug || label.trim(),
        label,
      });
    });
    setValue(fieldName, next, {shouldValidate: true, shouldDirty: true});
    Message.success(i18n('common.flatPasteSuccess', labels.length));
  };

  const startFlatPasteFromText = (rawText: string, fieldName: string) => {
    const rows = parseClipboardRows(rawText);
    const current = getValues(fieldName);
    const existingLabels = new Set(
      (Array.isArray(current) ? current : []).map((item) => `${item?.label ?? ''}`.trim()),
    );
    const labels: string[] = [];
    rows.forEach((row) => {
      row.forEach((cell) => {
        const label = `${cell ?? ''}`.trim();
        if (!label || existingLabels.has(label) || labels.includes(label)) {
          return;
        }
        labels.push(label);
      });
    });
    if (!labels.length) {
      Message.success(i18n('common.flatPasteNone'));
      return;
    }
    setPendingTreePaste({
      mode: 'flat',
      raw: labels,
      fieldName,
      summary: i18n('common.flatPasteConfirmSummary', labels.length),
    });
  };

  const applyTreeFromClipboard = (clipboardData: any, options: any = {}) => {
    const {fieldName = `${name}.alternativas`, responseType = 'multipleChoice'} = options;
    const normalized = normalizeAlternativeNodes(clipboardData?.payload?.alternativas || []);
    setValue(`${name}.tipoRespuesta`, responseType);
    setValue(`${name}.alternativeTree`, true);
    setValue(fieldName, resetAlternativeIds(normalized));
    Message.success(i18n('common.treePasteSuccess'));
  };

  const startPasteFromText = (rawText: string, options: any = {}) => {
    const {target = null, rowsOnly = false, fieldName = `${name}.alternativas`, responseType = 'multipleChoice'} = options;
    let payload: any = null;
    try {
      payload = JSON.parse(rawText);
    } catch (error) {
      payload = null;
    }

    if (
      !rowsOnly &&
      payload &&
      payload.type === ALT_TREE_CLIPBOARD_TYPE &&
      payload.version === ALT_TREE_CLIPBOARD_VERSION &&
      payload.payload?.alternativas
    ) {
      const nodeCount = normalizeAlternativeNodes(payload.payload.alternativas).length;
      setPendingTreePaste({
        mode: 'tree',
        raw: payload,
        fieldName,
        responseType,
        summary: i18n('common.treePasteConfirmSummary', nodeCount),
      });
      return;
    }

    const rows = parseClipboardRows(rawText);
    if (!rows.length) {
      if (rowsOnly) {
        return;
      }
      Message.error(i18n('common.treePasteInvalidClipboard'));
      return;
    }

    if (!target) {
      Message.error(i18n('common.treePasteNeedFocusedNode'));
      return;
    }

    setPendingTreePaste({
      mode: 'rows',
      raw: rows,
      target,
      fieldName,
      summary: i18n('common.treePasteRowsConfirmSummary', rows.length),
    });
  };

  const doConfirmPendingTreePaste = () => {
    if (!pendingTreePaste) {
      return;
    }
    if (pendingTreePaste.mode === 'tree') {
      applyTreeFromClipboard(pendingTreePaste.raw, {
        fieldName: pendingTreePaste.fieldName,
        responseType: pendingTreePaste.responseType,
      });
    } else if (pendingTreePaste.mode === 'flat') {
      appendFlatAlternatives(pendingTreePaste.raw, pendingTreePaste.fieldName);
    } else {
      appendRowsToTreeNode(pendingTreePaste.raw, pendingTreePaste.target, pendingTreePaste.fieldName);
    }
    setPendingTreePaste(null);
  };

  const doCancelPendingTreePaste = () => {
    setPendingTreePaste(null);
  };

  const doCopyTreeToClipboard = async (fieldName: string, responseType: string) => {
    const alternativas = normalizeAlternativeNodes(getValues(fieldName) || []);
    const payload = {
      type: ALT_TREE_CLIPBOARD_TYPE,
      version: ALT_TREE_CLIPBOARD_VERSION,
      payload: {
        tipoRespuesta: responseType,
        alternativeTree: true,
        alternativas,
      },
    };
    try {
      await navigator.clipboard.writeText(JSON.stringify(payload));
      Message.success(i18n('common.treeCopySuccess'));
    } catch (error) {
      Message.error(i18n('common.treeCopyError'));
    }
  };

  const doPasteTree = async (fieldName: string, responseType: string) => {
    try {
      const clipboardText = await navigator.clipboard.readText();
      startPasteFromText(clipboardText, {rowsOnly: false, fieldName, responseType});
    } catch (error) {
      Message.error(i18n('common.treePasteClipboardReadError'));
    }
  };

  const shouldHandleRowsPaste = (clipboardText = '') => {
    return clipboardText.includes('\n') || clipboardText.includes('\t');
  };

  const handleTreeNodePaste = (event, target, fieldName: string, responseType: string) => {
    if (!watchAlternativeTree || watchTipoRespuesta !== responseType) {
      return;
    }
    const clipboardText = event.clipboardData?.getData('text/plain');
    if (!clipboardText || !shouldHandleRowsPaste(clipboardText)) {
      return;
    }
    event.preventDefault();
    startPasteFromText(clipboardText, {target, rowsOnly: true, fieldName, responseType});
  };

  const getChildrenArrayByParentPath = (tree, parentPath: number[]) => {
    if (!Array.isArray(tree)) {
      return null;
    }
    if (!parentPath.length) {
      return tree;
    }
    if (parentPath.length === 1) {
      const parent = tree[parentPath[0]];
      if (!parent) {
        return null;
      }
      if (!Array.isArray(parent.children)) {
        parent.children = [];
      }
      return parent.children;
    }
    if (parentPath.length === 2) {
      const parent = tree[parentPath[0]]?.children?.[parentPath[1]];
      if (!parent) {
        return null;
      }
      if (!Array.isArray(parent.children)) {
        parent.children = [];
      }
      return parent.children;
    }
    return null;
  };

  const adjustPathAfterRemoval = (destinationParentPath: number[], sourceNodePath: number[]) => {
    const sourceParentPath = sourceNodePath.slice(0, -1);
    const sourceIndex = sourceNodePath[sourceNodePath.length - 1];

    if (!startsWithPath(destinationParentPath, sourceParentPath)) {
      return destinationParentPath;
    }

    if (destinationParentPath.length <= sourceParentPath.length) {
      return destinationParentPath;
    }

    const affectedIndexPosition = sourceParentPath.length;
    const affectedIndex = destinationParentPath[affectedIndexPosition];
    if (affectedIndex > sourceIndex) {
      const adjusted = [...destinationParentPath];
      adjusted[affectedIndexPosition] = affectedIndex - 1;
      return adjusted;
    }
    return destinationParentPath;
  };

  const onDragEndTreeByField = (result, fieldName, treeValue) => {
    const {source, destination} = result;
    if (!destination) {
      return;
    }

    const sourceParentPath = parseDroppableParentPath(source.droppableId);
    const destinationParentPath = parseDroppableParentPath(destination.droppableId);

    if (!sourceParentPath || !destinationParentPath) {
      return;
    }

    const sourceNodePath = [...sourceParentPath, source.index];
    const movingNode = getNodeByPath(treeValue || [], sourceNodePath);
    if (!movingNode) {
      return;
    }

    if (startsWithPath(destinationParentPath, sourceNodePath)) {
      Message.error(i18n('common.dragAlternativeInvalidTarget'));
      return;
    }

    const movingSubtreeHeight = getSubtreeHeight(movingNode);
    const destinationDepth = destinationParentPath.length + 1;
    if (destinationDepth + movingSubtreeHeight - 1 > MAX_TREE_DEPTH) {
      Message.error(i18n('common.dragAlternativeDepthExceeded'));
      return;
    }

    const updated = JSON.parse(JSON.stringify(treeValue || []));
    const sourceArray = getChildrenArrayByParentPath(updated, sourceParentPath);
    if (!sourceArray || !sourceArray[source.index]) {
      return;
    }

    const [movedNode] = sourceArray.splice(source.index, 1);
    const adjustedDestinationParentPath = adjustPathAfterRemoval(destinationParentPath, sourceNodePath);
    const destinationArray = getChildrenArrayByParentPath(updated, adjustedDestinationParentPath);
    if (!destinationArray) {
      return;
    }

    const destinationIndex = Math.max(0, Math.min(destination.index, destinationArray.length));
    destinationArray.splice(destinationIndex, 0, movedNode);
    setValue(fieldName, updated);
    setTimeout(() => resetHorizontalScrollAfterDrag(), 0);
  };

  const onDragEndAlternativeTree = (result) => {
    onDragEndTreeByField(result, `${name}.alternativas`, watchAlternatives);
  };

  const onDragEndAlternativasCheckboxTree = (result) => {
    onDragEndTreeByField(result, `${name}.alternativasCheckbox`, watchAlternativasCheckbox);
  };

  const doCopyJob = async (original) => {
    const pregunta = {...original};
    const newTitulo = pregunta.pregunta?.titulo + ' Copia ' + moment().format('DD-MM-YYYY HH:mm:ss');
    pregunta.pregunta.titulo = newTitulo;
    pregunta.pregunta.label = newTitulo;
    delete pregunta.key;
    delete pregunta.id;
    append(pregunta);
    doCloseCopyConfirmModal();
  };

  const doOpenCopyConfirmModal = () => {
    const pregunta = getValues(`${name}`);
    setRecordToCopy(pregunta);
  };

  const moveUp = (index) => {
    if (index === 0) {
      return;
    }
    move(index, index - 1);
  };

  const moveDown = (index) => {
    if (index === fieldsLength - 1) {
      return;
    }
    move(index, index + 1);
  };

  const customSelectInput = ({
    label = undefined,
    name = undefined,
    isDisabled = false,
    getOptionValue = (option) => option.id,
    createOption = (newValue) => {
      const label = String(newValue ?? '');
      const slug = label.toLowerCase().replace(/\W/g, '');
      return {
        id: slug || label.trim(),
        label,
      };
    },
    endMargin = '43px',
  }: any) => {
    return (
      <>
        <div style={{display: 'flex', alignItems: 'start'}}>
          <div style={{width: '100%'}}>
            <TagsFormItem
              label={label}
              name={name}
              disabled={isDisabled}
              notFoundContent={i18n('common.writeAlternative')}
              getOptionValue={getOptionValue}
              createOption={createOption}
              onSpreadsheetPaste={isDisabled ? undefined : (clipboardText) => startFlatPasteFromText(clipboardText, name)}
            />
          </div>
          <div
            style={{
              width: endMargin,
            }}
          />
        </div>
      </>
    );
  };

  const renderQuestionSummary = (item, preguntaIndex) => {
    return (
      <div style={{display: 'inline-flex', width: '100%', cursor: 'default'}}>
        <div
          style={{
            width: '82%',
            //backgroundColor: 'rgb(255,255,255,.725)',
            borderRadius: '5px',
            paddingLeft: '12px',
            paddingRight: '12px',
            paddingBottom: '4px',
            marginBottom: 10,
          }}>
          <div onClick={($e) => $e.stopPropagation()}>
            <InputFormItem
              onChange={(newValue) => {
                //console.log('%c⧭ InputFormItem newValue', 'color: #00e600', newValue);
                setValue(`${name}.pregunta.label`, newValue);
              }}
              name={`${name}.pregunta.titulo`}
              defaultValue={item.pregunta?.titulo}
              label={'Pregunta ' + (preguntaIndex + 1)}
              variant="outlined"
              fontSize="1.15rem"
              labelFontSize="1.05rem"
              shrinkLabel={false}
            />
          </div>
        </div>
        <OnHoverBox style={{marginLeft: 'auto', alignSelf: 'center'}} onClick={($e) => $e.stopPropagation()}>
          <Tooltip style={{marginLeft: 10}} followCursor placement={'top-start'} title={i18n('common.copy')}>
            <IconButton size="small" color="primary" onClick={(_) => doOpenCopyConfirmModal()}>
              <FileCopy />
            </IconButton>
          </Tooltip>
          <Tooltip
            enterDelay={700}
            leaveDelay={150}
            enterNextDelay={400}
            followCursor
            placement={'top-start'}
            title={i18n('common.moveDown')}>
            <IconButton size="small" color="primary" onClick={() => moveDown(preguntaIndex)}>
              <ArrowDownward />
            </IconButton>
          </Tooltip>
          <Tooltip
            enterDelay={700}
            leaveDelay={150}
            enterNextDelay={400}
            followCursor
            placement={'top-start'}
            title={i18n('common.moveUp')}>
            <IconButton size="small" color="primary" onClick={() => moveUp(preguntaIndex)}>
              <ArrowUpward />
            </IconButton>
          </Tooltip>
          <Tooltip
            enterDelay={700}
            leaveDelay={150}
            enterNextDelay={400}
            followCursor
            placement={'top-start'}
            title={i18n('common.destroy')}>
            <IconButton
              size="small"
              color="secondary"
              onClick={() => {
                doOpenDeleteConfirmModal(preguntaIndex);
              }}>
              <Delete />
            </IconButton>
          </Tooltip>
        </OnHoverBox>
      </div>
    );
  };

  const materialClasses = useMaterialStyles();

  return (
    <>
      <CustomAccordion
        key={item.id}
        item={item}
        index={indexPregunta}
        uniqueKey={item.id}
        entityName="Pregunta"
        renderSummary={renderQuestionSummary}
        renderDetails={() => (
          <Grid
            container
            spacing={1}
            style={
              {
                //zoom: 1.05
              }
            }>
            {/* <Grid>
                <Grid item lg={12}>
                  <Grid style={{marginBottom: 15}} item>
                    <label style={{fontWeight: 'bold', fontSize: 14}}>Pregunta:</label>
                  </Grid>

                  <Grid style={{marginBottom: 15}} item>
                    <label style={{fontSize: 16, fontWeight: 700, marginTop: 10}}>
                      {watchPregunta?.label || watchPregunta?.titulo || ''}
                    </label>
                  </Grid>

                  <PreguntaAutocompleteFormItem
                    autoFocus={!watchPregunta?.id}
                    defaultValue={watchPregunta}
                    name={`${name}.pregunta`}
                    size={'small'}
                    showCreate={true}
                  />
                </Grid>
              </Grid> */}

            {/* <Grid container lg={12}>
            <Grid item>
              <SwitchFormItem name={`${name}.allowMultipleAnswers`} size={'medium'} />
            </Grid>
            <Grid item style={{alignSelf: 'center', marginTop: 5}}>
              <label style={{fontWeight: 'bold', fontSize: 16}}>Permitir Multiples Tipos de Respuesta</label>
            </Grid>
          </Grid> */}

            {watchName.allowMultipleAnswers ? (
              <Grid item lg={12}>
                <SelectFormItem
                  label="Tipo de Respuesta"
                  mode="multiple"
                  name={`${name}.multipleAnswersType`}
                  options={tipoRespuestaOptions}
                  endButtonMargin="43px"
                />
              </Grid>
            ) : (
              <Grid item lg={12}>
                <SelectFormItem
                  label="Tipo de Respuesta"
                  onChange={(value) => {
                    const tipoRespuestaIncidente = tipoRespuestaIncidenteOptions.find((item, index) => value == item.value);
                    if (tipoRespuestaIncidente) {
                      //console.log('%c⧭ tipoRespuestaIncidente selected', 'color: #1d3f73', tipoRespuestaIncidente);
                      setValue(`${name}.keyPointer`, tipoRespuestaIncidente.value);
                      setValue(`${name}.alternativeTree`, tipoRespuestaIncidente.alternativeTree);
                      setValue(`${name}.isMultiple`, false); //tipoRespuestaIncidente.value === 'incident_employee');
                      if (tipoRespuestaIncidente.responseType === 'multiCheckbox') {
                        setValue(`${name}.alternativasCheckbox`, tipoRespuestaIncidente.subOptions || []);
                        setValue(`${name}.allowOthersOption`, Boolean(tipoRespuestaIncidente.allowOthersOption));
                      } else {
                        setValue(`${name}.alternativas`, tipoRespuestaIncidente.subOptions);
                      }
                    } else {
                      setValue(`${name}.keyPointer`, null);
                    }
                  }}
                  name={`${name}.tipoRespuesta`}
                  options={tipoRespuestaOptions}
                  endButtonMargin="43px"
                />
              </Grid>
            )}

            {['location', 'thirdParty', 'asset', 'perfilEmpleado', 'usuario', 'project', 'asignadorFuncion'].includes(
              watchTipoRespuesta,
            ) && (
              <Grid item lg={12}>
                <CheckboxFormItem label="Selección múltiple" name={`${name}.isMultiple`} size={'medium'} />
                {['location', 'thirdParty', 'asset', 'perfilEmpleado', 'usuario', 'project'].includes(
                  watchTipoRespuesta,
                ) && (
                  <CheckboxFormItem label="Restringir por Ubicación" name={`${name}.restrictByLocation`} size={'medium'} />
                )}
              </Grid>
            )}

            {/* {['location', 'thirdParty', 'perfilEmpleado'].includes(watchTipoRespuesta) && (
              <Grid item lg={12}>
                <CheckboxFormItem label="Restringir por Ubicación" name={`${name}.restrictByLocation`} size={'medium'} />
              </Grid>
            )} */}

            {((watchTipoRespuesta === 'meterReading' && !watchName.allowMultipleAnswers) ||
              (watchName.multipleAnswersType?.includes('meterReading') && watchName.allowMultipleAnswers)) && (
              <Grid item lg={12}>
                <UnidadMedidaAutocompleteFormItem
                  label="Seleccione Unidad de Medida a Medir"
                  name={`${name}.unidadMedida`}
                  size={'small'}
                  showCreate={true}
                />
              </Grid>
            )}

            {((watchTipoRespuesta === 'multiCheckbox' && !watchName.allowMultipleAnswers) ||
              (watchName.multipleAnswersType?.includes('multiCheckbox') && watchName.allowMultipleAnswers)) && (
              <Grid item lg={12}>
                {!watchAlternativeTree &&
                  customSelectInput({
                    label: 'Alternativas de Checkbox',
                    name: `${name}.alternativasCheckbox`,
                  })}
                {/* <RespuestaAutocompleteFormItem
                  label="Alternativas de Checkbox"
                  name={`${name}.alternativasCheckbox`}
                  size={'small'}
                  showCreate={true}
                  mode="multiple"
                /> */}
              </Grid>
            )}

            {tipoRespuestaIncidenteOptions.map((item, index) => {
              const template: any[] = [];
              if (item.value === watchTipoRespuesta && item.responseType === 'multipleChoice' && !item.alternativeTree) {
                template.push(
                  <React.Fragment key={index}>
                    {item.responseType === 'multipleChoice' && (
                      <Grid item lg={12}>
                        {customSelectInput({
                          label: 'Alternativas de Respuestas',
                          name: `${name}.alternativas`,
                          isDisabled: true,
                        })}
                      </Grid>
                    )}
                  </React.Fragment>,
                );
              }
              if (item.value === watchTipoRespuesta && item.responseType === 'multipleChoice' && item.alternativeTree) {
                template.push(
                  <Box pl={1} display="flex" flexDirection="column" gap={1}>
                    {watchAlternatives?.map((alt, i) => (
                      <Box key={alt.id} pl={1} borderLeft="2px solid #ccc" mb={1}>
                        <Typography variant="subtitle2" gutterBottom>
                          {getValues(`${name}.alternativas.${i}.label`)}
                        </Typography>
                        {/* Nivel 2 */}
                        <Box pl={2}>
                          {alt.children?.map((child, j) => (
                            <Box key={child.id} pl={1} borderLeft="2px dashed #aaa" mb={1}>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Typography variant="body2" gutterBottom>
                                  {getValues(`${name}.alternativas.${i}.children.${j}.label`)}
                                </Typography>
                              </Box>
                              {/* Nivel 3 */}
                              <Box pl={2}>
                                {child.children?.map((sub, k) => (
                                  <Box key={sub.id} pl={1} borderLeft="2px dotted #888" mb={1}>
                                    <Box display="flex" alignItems="center" gap={1}>
                                      <Typography variant="body2" gutterBottom>
                                        {getValues(`${name}.alternativas.${i}.children.${j}.children.${k}.label`)}
                                      </Typography>
                                    </Box>
                                  </Box>
                                ))}
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    ))}
                  </Box>,
                );
              }
              return template;
            })}

            {((watchTipoRespuesta === 'multipleChoice' && !watchName.allowMultipleAnswers) ||
              (watchName.multipleAnswersType?.includes('multipleChoice') && watchName.allowMultipleAnswers)) && (
              <React.Fragment>
                {/* <Grid item lg={12}>
                  <RespuestaAutocompleteFormItem
                    label="Respuesta Correcta"
                    name={`${name}.respuestaCorrecta`}
                    size={'small'}
                    showCreate={true}
                  />
                </Grid> */}
                <Grid item lg={12}>
                  {customSelectInput({
                    label: 'Alternativas de Respuestas',
                    name: `${name}.alternativas`,
                  })}
                </Grid>
              </React.Fragment>
            )}
            {watchTipoRespuesta === 'numberField' && (
              <>
                <Box display={'flex'} flexDirection={'row'} pl={1}>
                  <Grid item lg={12}>
                    <CheckboxFormItem
                      label={i18n('common.alertNumberRange')}
                      name={`${name}.hasAlertNumberRange`}
                      size={'medium'}
                    />
                  </Grid>
                  {watchHasAlertNumberRange && (
                    <>
                      <Grid item lg={12}>
                        <InputNumberRangeFormItem label={''} name={`${name}.alertNumberRange`} />
                      </Grid>

                      <Grid item lg={12}>
                        <CheckboxFormItem
                          label={i18n('common.invertAlertNumberRange')}
                          name={`${name}.invertAlertNumberRange`}
                          size={'medium'}
                        />
                      </Grid>
                    </>
                  )}
                </Box>
                <Grid container spacing={2} pl={1} pr={1}>
                  <Grid item lg={3} md={3} sm={6} xs={6}>
                    <InputNumberFormItem
                      label={'Mínimo'}
                      name={`${name}.numberFieldMin`}
                      placeholder={'Dejar en blanco para sin mínimo'}
                    />
                  </Grid>
                  <Grid item lg={3} md={3} sm={6} xs={6}>
                    <InputNumberFormItem
                      label={'Máximo'}
                      name={`${name}.numberFieldMax`}
                      placeholder={'Dejar en blanco para sin máximo'}
                    />
                  </Grid>
                </Grid>
              </>
            )}

            {watchTipoRespuesta === 'ammount' && (
              <Grid container spacing={2} pl={1} pr={1}>
                <Grid item lg={6} md={6} sm={12} xs={12}>
                  <InputNumberFormItem
                    label={'Mínimo'}
                    name={`${name}.ammountMin`}
                    placeholder={'Dejar en blanco para sin mínimo'}
                  />
                </Grid>
                <Grid item lg={6} md={6} sm={12} xs={12}>
                  <InputNumberFormItem
                    label={'Máximo'}
                    name={`${name}.ammountMax`}
                    placeholder={'Dejar en blanco para sin máximo'}
                  />
                </Grid>
              </Grid>
            )}

            {(([
              'multipleChoice',
              'incident_causaInmediata1',
              'incident_causaInmediata2',
              'incident_causaInmediata3',
              'incident_causaBasica1',
              'incident_causaBasica2',
              'incident_causaBasica3',
            ].includes(watchTipoRespuesta) &&
              !watchName.allowMultipleAnswers) ||
              (watchName.multipleAnswersType?.includes('multipleChoice') && watchName.allowMultipleAnswers) ||
              (watchTipoRespuesta === 'multiCheckbox' && !watchName.allowMultipleAnswers) ||
              (watchName.multipleAnswersType?.includes('multiCheckbox') && watchName.allowMultipleAnswers)) && (
              <>
                <Box display={'flex'} flexDirection={'row'} pl={1}>
                  {((watchTipoRespuesta === 'multiCheckbox' && !watchName.allowMultipleAnswers) ||
                    (watchName.multipleAnswersType?.includes('multiCheckbox') && watchName.allowMultipleAnswers)) && (
                    <Grid item lg={12}>
                      <CheckboxFormItem label={i18n('common.uniqueOption')} name={`${name}.onlyOneOption`} size={'medium'} />
                    </Grid>
                  )}
                  <Grid item lg={12}>
                    <CheckboxFormItem
                      label={i18n('common.allowOptionOthers')}
                      name={`${name}.allowOthersOption`}
                      size={'medium'}
                    />
                  </Grid>
                  {(watchTipoRespuesta === 'multipleChoice' || watchTipoRespuesta === 'multiCheckbox') && (
                    <Grid item lg={12}>
                      <CheckboxFormItem
                        label={i18n('common.alternativeTree')}
                        name={`${name}.alternativeTree`}
                        size={'medium'}
                        onChange={(value) => {
                          if (!value) {
                            if (watchTipoRespuesta === 'multiCheckbox') {
                              setValue(
                                `${name}.alternativasCheckbox`,
                                watchAlternativasCheckbox?.map((alt) => {
                                  alt.children = [];
                                  return alt;
                                }),
                              );
                            } else {
                              setValue(
                                `${name}.alternativas`,
                                watchAlternatives?.map((alt) => {
                                  alt.children = [];
                                  return alt;
                                }),
                              );
                            }
                          }
                        }}
                      />
                    </Grid>
                  )}
                  <Grid item lg={12}>
                    <CheckboxFormItem
                      label={i18n('common.defaultFirstOption')}
                      name={`${name}.defaultFirstOption`}
                      size={'medium'}
                    />
                  </Grid>
                </Box>
                {watchTipoRespuesta === 'multipleChoice' && watchAlternativeTree && (
                  <Box pl={1} display="flex" flexDirection="column" gap={1}>
                    <Grid item lg={12}>
                      <SelectFormItem
                        label={i18n('common.alternativeTreeDisplayLevels')}
                        name={`${name}.alternativeTreeDisplayLevels`}
                        options={[
                          {value: '', label: i18n('common.alternativeTreeDisplayLevelsDefault')},
                          {value: 1, label: i18n('common.alternativeTreeDisplayLevels1')},
                          {value: 2, label: i18n('common.alternativeTreeDisplayLevels2')},
                          {value: 3, label: i18n('common.alternativeTreeDisplayLevels3')},
                        ]}
                        onChange={(value) => {
                          setValue(
                            `${name}.alternativeTreeDisplayLevels`,
                            value === '' || value === null || value === undefined ? null : Number(value),
                          );
                        }}
                        endButtonMargin="43px"
                      />
                    </Grid>
                    <Box display="flex" flexDirection="row" gap={1} mb={1} flexWrap="wrap">
                      <Button
                        type="button"
                        variant="outlined"
                        color="primary"
                        onClick={() => doCopyTreeToClipboard(`${name}.alternativas`, 'multipleChoice')}>
                        {i18n('common.copyTree')}
                      </Button>
                      <Button
                        type="button"
                        variant="outlined"
                        color="primary"
                        startIcon={<ContentPaste />}
                        onClick={() => doPasteTree(`${name}.alternativas`, 'multipleChoice')}>
                        {i18n('common.pasteTree')}
                      </Button>
                    </Box>
                    <DragDropContext onDragEnd={onDragEndAlternativeTree}>
                      <Droppable droppableId={ROOT_DROPPABLE_ID} type="ALT_TREE_NODE">
                        {(rootProvided) => (
                          <Box ref={rootProvided.innerRef} {...rootProvided.droppableProps}>
                            {watchAlternatives?.map((alt, i) => (
                              <Draggable key={`l1:${alt.id}`} draggableId={`l1:${alt.id}`} index={i}>
                                {(level1Provided) => (
                                  <Box
                                    ref={level1Provided.innerRef}
                                    {...level1Provided.draggableProps}
                                    style={level1Provided.draggableProps.style}
                                    pl={1}
                                    borderLeft="2px solid #ccc"
                                    mb={1}>
                                    <Box display="flex" alignItems="center" gap={1}>
                                      <IconButton size="small" {...level1Provided.dragHandleProps}>
                                        <DragIndicator sx={{fontSize: 16, opacity: 0.7}} />
                                      </IconButton>
                                      <Box
                                        flex={1}
                                        onPaste={(event) =>
                                          handleTreeNodePaste(event, {level: 1, i}, `${name}.alternativas`, 'multipleChoice')
                                        }>
                                        <InputFormItem
                                          label={`Alternativa ${i + 1}`}
                                          name={`${name}.alternativas.${i}.label`}
                                          defaultValue={alt.label}
                                          onChange={(newLabel) => {
                                            const updated = [...watchAlternatives];
                                            updated[i].label = newLabel;
                                            setValue(`${name}.alternativas`, updated);
                                          }}
                                        />
                                      </Box>
                                    </Box>
                                    <Box pl={2}>
                                      <Droppable droppableId={`l1:${i}`} type="ALT_TREE_NODE">
                                        {(level2DropProvided) => (
                                          <Box ref={level2DropProvided.innerRef} {...level2DropProvided.droppableProps}>
                                            {alt.children?.map((child, j) => (
                                              <Draggable key={`l2:${child.id}`} draggableId={`l2:${child.id}`} index={j}>
                                                {(level2Provided) => (
                                                  <Box
                                                    ref={level2Provided.innerRef}
                                                    {...level2Provided.draggableProps}
                                                    style={level2Provided.draggableProps.style}
                                                    pl={1}
                                                    borderLeft="2px dashed #aaa"
                                                    mb={1}>
                                                    <Box display="flex" alignItems="center" gap={1}>
                                                      <IconButton size="small" {...level2Provided.dragHandleProps}>
                                                        <DragIndicator sx={{fontSize: 16, opacity: 0.7}} />
                                                      </IconButton>
                                                      <Box
                                                        flex={1}
                                                        onPaste={(event) =>
                                                          handleTreeNodePaste(
                                                            event,
                                                            {level: 2, i, j},
                                                            `${name}.alternativas`,
                                                            'multipleChoice',
                                                          )
                                                        }>
                                                        <InputFormItem
                                                          label={`${i + 1}.${j + 1}`}
                                                          name={`${name}.alternativas.${i}.children.${j}.label`}
                                                          defaultValue={child.label}
                                                          onChange={(newLabel) => {
                                                            const updated = [...watchAlternatives];
                                                            updated[i].children[j].label = newLabel;
                                                            setValue(`${name}.alternativas`, updated);
                                                          }}
                                                        />
                                                      </Box>
                                                      <IconButton
                                                        size="small"
                                                        color="secondary"
                                                        onClick={() => {
                                                          const updated = [...watchAlternatives];
                                                          if (updated[i].children.length === 1) {
                                                            updated[i].children = [];
                                                          } else {
                                                            updated[i].children.splice(j, 1);
                                                          }
                                                          setValue(`${name}.alternativas`, updated);
                                                        }}>
                                                        <DeleteOutline />
                                                      </IconButton>
                                                    </Box>
                                                    <Box pl={2}>
                                                      <Droppable droppableId={`l2:${i}:${j}`} type="ALT_TREE_NODE">
                                                        {(level3DropProvided) => (
                                                          <Box
                                                            ref={level3DropProvided.innerRef}
                                                            {...level3DropProvided.droppableProps}>
                                                            {child.children?.map((sub, k) => (
                                                              <Draggable
                                                                key={`l3:${sub.id}`}
                                                                draggableId={`l3:${sub.id}`}
                                                                index={k}>
                                                                {(level3Provided) => (
                                                                  <Box
                                                                    ref={level3Provided.innerRef}
                                                                    {...level3Provided.draggableProps}
                                                                    style={level3Provided.draggableProps.style}
                                                                    pl={1}
                                                                    borderLeft="2px dotted #888"
                                                                    mb={1}>
                                                                    <Box display="flex" alignItems="center" gap={1}>
                                                                      <IconButton
                                                                        size="small"
                                                                        {...level3Provided.dragHandleProps}>
                                                                        <DragIndicator sx={{fontSize: 16, opacity: 0.7}} />
                                                                      </IconButton>
                                                                      <Box
                                                                        flex={1}
                                                                        onPaste={(event) =>
                                                                          handleTreeNodePaste(
                                                                            event,
                                                                            {level: 3, i, j, k},
                                                                            `${name}.alternativas`,
                                                                            'multipleChoice',
                                                                          )
                                                                        }>
                                                                        <InputFormItem
                                                                          label={`${i + 1}.${j + 1}.${k + 1}`}
                                                                          name={`${name}.alternativas.${i}.children.${j}.children.${k}.label`}
                                                                          defaultValue={sub.label}
                                                                          onChange={(newLabel) => {
                                                                            const updated = [...watchAlternatives];
                                                                            updated[i].children[j].children[k].label = newLabel;
                                                                            setValue(`${name}.alternativas`, updated);
                                                                          }}
                                                                        />
                                                                      </Box>
                                                                      <IconButton
                                                                        size="small"
                                                                        color="secondary"
                                                                        onClick={() => {
                                                                          const updated = [...watchAlternatives];
                                                                          if (updated[i].children[j].children.length === 1) {
                                                                            updated[i].children[j].children = [];
                                                                          } else {
                                                                            updated[i].children[j].children.splice(k, 1);
                                                                          }
                                                                          setValue(`${name}.alternativas`, updated);
                                                                        }}>
                                                                        <DeleteOutline />
                                                                      </IconButton>
                                                                    </Box>
                                                                  </Box>
                                                                )}
                                                              </Draggable>
                                                            ))}
                                                            {level3DropProvided.placeholder}
                                                          </Box>
                                                        )}
                                                      </Droppable>
                                                      <Box pl={1}>
                                                        <Button
                                                          type="button"
                                                          onClick={() => {
                                                            const updated = [...watchAlternatives];
                                                            const newSub = {id: Date.now(), label: '', children: []};
                                                            if (!updated[i].children[j].children)
                                                              updated[i].children[j].children = [];
                                                            updated[i].children[j].children.push(newSub);
                                                            setValue(`${name}.alternativas`, updated);
                                                          }}>
                                                          + {i + 1}.{j + 1}.
                                                          {(watchAlternatives[i].children[j].children?.length || 0) + 1}
                                                        </Button>
                                                      </Box>
                                                    </Box>
                                                  </Box>
                                                )}
                                              </Draggable>
                                            ))}
                                            {level2DropProvided.placeholder}
                                          </Box>
                                        )}
                                      </Droppable>
                                      <Button
                                        type="button"
                                        onClick={() => {
                                          const updated = [...watchAlternatives];
                                          const newChild = {id: Date.now(), label: '', children: []};
                                          if (!updated[i].children) updated[i].children = [];
                                          updated[i].children.push(newChild);
                                          setValue(`${name}.alternativas`, updated);
                                        }}>
                                        + {i + 1}.{(watchAlternatives[i].children?.length || 0) + 1}
                                      </Button>
                                    </Box>
                                  </Box>
                                )}
                              </Draggable>
                            ))}
                            {rootProvided.placeholder}
                          </Box>
                        )}
                      </Droppable>
                    </DragDropContext>
                    <Box>
                      <Button
                        type="button"
                        onClick={() => {
                          const updated = [...(watchAlternatives || [])];
                          updated.push({id: Date.now(), label: '', children: []});
                          setValue(`${name}.alternativas`, updated);
                        }}>
                        + Nueva alternativa
                      </Button>
                    </Box>
                  </Box>
                )}
                {watchTipoRespuesta === 'multiCheckbox' && watchAlternativeTree && (
                  <Box pl={1} display="flex" flexDirection="column" gap={1}>
                    <Box display="flex" flexDirection="row" gap={1} mb={1} flexWrap="wrap">
                      <Button
                        type="button"
                        variant="outlined"
                        color="primary"
                        onClick={() => doCopyTreeToClipboard(`${name}.alternativasCheckbox`, 'multiCheckbox')}>
                        {i18n('common.copyTree')}
                      </Button>
                      <Button
                        type="button"
                        variant="outlined"
                        color="primary"
                        startIcon={<ContentPaste />}
                        onClick={() => doPasteTree(`${name}.alternativasCheckbox`, 'multiCheckbox')}>
                        {i18n('common.pasteTree')}
                      </Button>
                    </Box>
                    <DragDropContext onDragEnd={onDragEndAlternativasCheckboxTree}>
                      <Droppable droppableId={ROOT_DROPPABLE_ID} type="ALT_TREE_NODE">
                        {(rootProvided) => (
                          <Box ref={rootProvided.innerRef} {...rootProvided.droppableProps}>
                            {watchAlternativasCheckbox?.map((alt, i) => (
                              <Draggable key={`mc_l1:${alt.id}`} draggableId={`mc_l1:${alt.id}`} index={i}>
                                {(level1Provided) => (
                                  <Box
                                    ref={level1Provided.innerRef}
                                    {...level1Provided.draggableProps}
                                    style={level1Provided.draggableProps.style}
                                    pl={1}
                                    borderLeft="2px solid #ccc"
                                    mb={1}>
                                    <Box display="flex" alignItems="center" gap={1}>
                                      <IconButton size="small" {...level1Provided.dragHandleProps}>
                                        <DragIndicator sx={{fontSize: 16, opacity: 0.7}} />
                                      </IconButton>
                                      <Box
                                        flex={1}
                                        onPaste={(event) =>
                                          handleTreeNodePaste(
                                            event,
                                            {level: 1, i},
                                            `${name}.alternativasCheckbox`,
                                            'multiCheckbox',
                                          )
                                        }>
                                        <InputFormItem
                                          label={`Alternativa ${i + 1}`}
                                          name={`${name}.alternativasCheckbox.${i}.label`}
                                          defaultValue={alt.label}
                                          onChange={(newLabel) => {
                                            const updated = [...watchAlternativasCheckbox];
                                            updated[i].label = newLabel;
                                            setValue(`${name}.alternativasCheckbox`, updated);
                                          }}
                                        />
                                      </Box>
                                    </Box>
                                    <Box pl={2}>
                                      <Droppable droppableId={`l1:${i}`} type="ALT_TREE_NODE">
                                        {(level2DropProvided) => (
                                          <Box ref={level2DropProvided.innerRef} {...level2DropProvided.droppableProps}>
                                            {alt.children?.map((child, j) => (
                                              <Draggable key={`mc_l2:${child.id}`} draggableId={`mc_l2:${child.id}`} index={j}>
                                                {(level2Provided) => (
                                                  <Box
                                                    ref={level2Provided.innerRef}
                                                    {...level2Provided.draggableProps}
                                                    style={level2Provided.draggableProps.style}
                                                    pl={1}
                                                    borderLeft="2px dashed #aaa"
                                                    mb={1}>
                                                    <Box display="flex" alignItems="center" gap={1}>
                                                      <IconButton size="small" {...level2Provided.dragHandleProps}>
                                                        <DragIndicator sx={{fontSize: 16, opacity: 0.7}} />
                                                      </IconButton>
                                                      <Box
                                                        flex={1}
                                                        onPaste={(event) =>
                                                          handleTreeNodePaste(
                                                            event,
                                                            {level: 2, i, j},
                                                            `${name}.alternativasCheckbox`,
                                                            'multiCheckbox',
                                                          )
                                                        }>
                                                        <InputFormItem
                                                          label={`${i + 1}.${j + 1}`}
                                                          name={`${name}.alternativasCheckbox.${i}.children.${j}.label`}
                                                          defaultValue={child.label}
                                                          onChange={(newLabel) => {
                                                            const updated = [...watchAlternativasCheckbox];
                                                            updated[i].children[j].label = newLabel;
                                                            setValue(`${name}.alternativasCheckbox`, updated);
                                                          }}
                                                        />
                                                      </Box>
                                                      <IconButton
                                                        size="small"
                                                        color="secondary"
                                                        onClick={() => {
                                                          const updated = [...watchAlternativasCheckbox];
                                                          if (updated[i].children.length === 1) {
                                                            updated[i].children = [];
                                                          } else {
                                                            updated[i].children.splice(j, 1);
                                                          }
                                                          setValue(`${name}.alternativasCheckbox`, updated);
                                                        }}>
                                                        <DeleteOutline />
                                                      </IconButton>
                                                    </Box>
                                                    <Box pl={2}>
                                                      <Droppable droppableId={`l2:${i}:${j}`} type="ALT_TREE_NODE">
                                                        {(level3DropProvided) => (
                                                          <Box
                                                            ref={level3DropProvided.innerRef}
                                                            {...level3DropProvided.droppableProps}>
                                                            {child.children?.map((sub, k) => (
                                                              <Draggable
                                                                key={`mc_l3:${sub.id}`}
                                                                draggableId={`mc_l3:${sub.id}`}
                                                                index={k}>
                                                                {(level3Provided) => (
                                                                  <Box
                                                                    ref={level3Provided.innerRef}
                                                                    {...level3Provided.draggableProps}
                                                                    style={level3Provided.draggableProps.style}
                                                                    pl={1}
                                                                    borderLeft="2px dotted #888"
                                                                    mb={1}>
                                                                    <Box display="flex" alignItems="center" gap={1}>
                                                                      <IconButton
                                                                        size="small"
                                                                        {...level3Provided.dragHandleProps}>
                                                                        <DragIndicator sx={{fontSize: 16, opacity: 0.7}} />
                                                                      </IconButton>
                                                                      <Box
                                                                        flex={1}
                                                                        onPaste={(event) =>
                                                                          handleTreeNodePaste(
                                                                            event,
                                                                            {level: 3, i, j, k},
                                                                            `${name}.alternativasCheckbox`,
                                                                            'multiCheckbox',
                                                                          )
                                                                        }>
                                                                        <InputFormItem
                                                                          label={`${i + 1}.${j + 1}.${k + 1}`}
                                                                          name={`${name}.alternativasCheckbox.${i}.children.${j}.children.${k}.label`}
                                                                          defaultValue={sub.label}
                                                                          onChange={(newLabel) => {
                                                                            const updated = [...watchAlternativasCheckbox];
                                                                            updated[i].children[j].children[k].label = newLabel;
                                                                            setValue(`${name}.alternativasCheckbox`, updated);
                                                                          }}
                                                                        />
                                                                      </Box>
                                                                      <IconButton
                                                                        size="small"
                                                                        color="secondary"
                                                                        onClick={() => {
                                                                          const updated = [...watchAlternativasCheckbox];
                                                                          if (updated[i].children[j].children.length === 1) {
                                                                            updated[i].children[j].children = [];
                                                                          } else {
                                                                            updated[i].children[j].children.splice(k, 1);
                                                                          }
                                                                          setValue(`${name}.alternativasCheckbox`, updated);
                                                                        }}>
                                                                        <DeleteOutline />
                                                                      </IconButton>
                                                                    </Box>
                                                                  </Box>
                                                                )}
                                                              </Draggable>
                                                            ))}
                                                            {level3DropProvided.placeholder}
                                                          </Box>
                                                        )}
                                                      </Droppable>
                                                      <Box pl={1}>
                                                        <Button
                                                          type="button"
                                                          onClick={() => {
                                                            const updated = [...watchAlternativasCheckbox];
                                                            const newSub = {id: Date.now(), label: '', children: []};
                                                            if (!updated[i].children[j].children)
                                                              updated[i].children[j].children = [];
                                                            updated[i].children[j].children.push(newSub);
                                                            setValue(`${name}.alternativasCheckbox`, updated);
                                                          }}>
                                                          + {i + 1}.{j + 1}.
                                                          {(watchAlternativasCheckbox[i].children[j].children?.length || 0) + 1}
                                                        </Button>
                                                      </Box>
                                                    </Box>
                                                  </Box>
                                                )}
                                              </Draggable>
                                            ))}
                                            {level2DropProvided.placeholder}
                                          </Box>
                                        )}
                                      </Droppable>
                                      <Button
                                        type="button"
                                        onClick={() => {
                                          const updated = [...watchAlternativasCheckbox];
                                          const newChild = {id: Date.now(), label: '', children: []};
                                          if (!updated[i].children) updated[i].children = [];
                                          updated[i].children.push(newChild);
                                          setValue(`${name}.alternativasCheckbox`, updated);
                                        }}>
                                        + {i + 1}.{(watchAlternativasCheckbox[i].children?.length || 0) + 1}
                                      </Button>
                                    </Box>
                                  </Box>
                                )}
                              </Draggable>
                            ))}
                            {rootProvided.placeholder}
                          </Box>
                        )}
                      </Droppable>
                    </DragDropContext>
                    <Box>
                      <Button
                        type="button"
                        onClick={() => {
                          const updated = [...(watchAlternativasCheckbox || [])];
                          updated.push({id: Date.now(), label: '', children: []});
                          setValue(`${name}.alternativasCheckbox`, updated);
                        }}>
                        + Nueva alternativa
                      </Button>
                    </Box>
                  </Box>
                )}
              </>
            )}

            {(([
              'date',
              ...tipoRespuestaIncidenteOptions.filter((tr) => tr.responseType === 'date').map((tr) => tr.value),
            ].includes(watchTipoRespuesta) &&
              !watchName.allowMultipleAnswers) ||
              (watchName.multipleAnswersType?.includes('date') && watchName.allowMultipleAnswers)) && (
              <Grid item lg={12}>
                <CheckboxFormItem label={i18n('common.allowOlderDates')} name={`${name}.allowDateBefore`} size={'medium'} />
              </Grid>
            )}

            <Grid item lg={12}>
              <div style={{border: '1px solid rgb(0,0,0,.225', borderRadius: 5, marginTop: 10, marginBottom: 16}}>
                <CheckboxFormItem label={i18n('common.answerRequired')} name={`${name}.isRequired`} size={'medium'} />
                <CheckboxFormItem label={i18n('common.allowNotApplicable')} name={`${name}.allowNotApplicable`} size={'medium'} />
                <CheckboxFormItem label={i18n('common.attachPhotos')} name={`${name}.havePhotos`} size={'medium'} />
                <CheckboxFormItem label={i18n('common.attachDocuments')} name={`${name}.haveFiles`} size={'medium'} />
                {['files', 'textFromImage'].includes(watchTipoRespuesta) && (
                  <>
                    {watchTipoRespuesta === 'files' && (
                      <CheckboxFormItem label={i18n('common.onlyImages')} name={`${name}.imagesOnly`} size={'medium'} />
                    )}
                    <CheckboxFormItem label={i18n('common.onlyFromCamera')} name={`${name}.cameraOnly`} size={'medium'} />
                  </>
                )}
                <CheckboxFormItem label={i18n('common.addComment')} name={`${name}.tieneComentario`} size={'medium'} />
                <CheckboxFormItem label={i18n('common.addCriticality')} name={`${name}.haveCriticality`} size={'medium'} />
                <CheckboxFormItem label={i18n('common.hiddenInPdf')} name={`${name}.hiddenInPdf`} size={'medium'} />
                {/* <div style={{marginTop: -11, display: 'inline-flex', alignItems: 'center', marginRight: 16}}>
                  <div style={{width: '100%', marginRight: -20}}>
                    <CheckboxFormItem label="Asignar Puntaje" name={`${name}.tienePuntaje`} size={'medium'} />
                  </div>
                  {watchTienePuntaje ? (
                    <InputNumberFormItem
                      label="Puntaje"
                      name={`${name}.puntaje`}
                      defaultValue={item.puntaje}
                      placeholder="Puntaje Pregunta"
                    />
                  ) : null}
                </div> */}
                <div
                  style={{
                    marginTop: -11,
                    display: 'inline-flex',
                    alignItems: 'center',
                    width: '-webkit-fill-available',
                    maxWidth: '90%',
                  }}>
                  <div style={{textWrap: 'nowrap'} as any}>
                    <CheckboxFormItem label="Adjuntar un enlace" name={`${name}.tieneLink`} size={'medium'} />
                  </div>
                  {watchTieneLink ? (
                    <div style={{width: '-webkit-fill-available'}}>
                      <InputFormItem
                        label="Link"
                        name={`${name}.link`}
                        defaultValue={item.link}
                        placeholder="https://www.ejemplo.com"
                      />
                    </div>
                  ) : null}
                  {watchTieneComentario ? (
                    <div style={{width: '-webkit-fill-available'}}>
                      <InputFormItem
                        label="Título Comentario (Opcional)"
                        name={`${name}.commentLabel`}
                        defaultValue={item.commentLabel}
                        placeholder={i18n('common.additionalComments')}
                      />
                    </div>
                  ) : null}
                </div>
              </div>
            </Grid>
            {/* <Grid item lg={12}>
              <InputFormItem label="Link" name={`${name}.link`} defaultValue={item.link} placeholder="https://www.ejemplo.com" />
            </Grid> */}
          </Grid>
        )}
        expanded={expanded}
        //detailTitle={`${item.nPregunta}. ${watchPregunta?.label || watchPregunta?.titulo || ''}`}
        handleChange={handleChangeAccordion}
        topButtonClick={(item, index) => {
          insert(index);
        }}
        bottomButtonClick={(item, index) => {
          insert(index + 1);
        }}
      />
      {recordToCopy && (
        <ConfirmModal
          title={i18n('common.areYouSureCopyPregunta')}
          onConfirm={() => doCopyJob(recordToCopy)}
          onClose={() => doCloseCopyConfirmModal()}
          okText={i18n('common.yes')}
          cancelText={i18n('common.no')}
        />
      )}
      {pendingTreePaste && (
        <ConfirmModal
          title={pendingTreePaste.summary}
          onConfirm={doConfirmPendingTreePaste}
          onClose={doCancelPendingTreePaste}
          okText={i18n('common.yes')}
          cancelText={i18n('common.no')}
        />
      )}
      {(recordToDelete || recordToDelete === 0) && (
        <ConfirmModal
          title={i18n('common.areYouSureDelete')}
          onConfirm={() => doDeleteRecord(recordToDelete)}
          onClose={() => doCloseDeleteConfirmModal()}
          okText={i18n('common.yes')}
          cancelText={i18n('common.no')}
        />
      )}
    </>
  );
}
